import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { AuthService } from './shared/auth/auth.service';
import { Subscription } from 'rxjs';
import { isMiniMenuHost, miniMenuDefaultSlug } from './mini-menu-host';
import { PageTitleService } from './services/page-title.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'Bliss';
  isLogin: boolean = false;
  isSpecialPage: boolean = false;
  isTourPage = /^\/tour(?:\/|$)/.test(window.location.pathname);
  isMiniMenuPage: boolean = isMiniMenuHost();
  // The footer stays hidden until the body content has actually filled in, so the page
  // never shows a header + footer wrapped around an empty middle while the user waits.
  footerReady: boolean = false;
  userName: any;
  private subscriptions: Subscription = new Subscription();

  @ViewChild('mainContent') private mainContentRef?: ElementRef<HTMLElement>;
  private contentObserver?: ResizeObserver;
  private contentSettleTimer: any;
  private footerCapTimer: any;

  constructor(
    private authService: AuthService,
    private router: Router,
    private zone: NgZone,
    private pageTitle: PageTitleService
  ) {}

  ngOnInit() {
    // Subscribe to router events
    this.subscriptions.add(
      this.router.events.subscribe((event: any) => {
        // Static title per route. GuardsCheckEnd precedes component creation, so a
        // page that knows better (business name, city listing) sets its own title
        // afterwards and wins. MiniMenu keeps its own title.
        if (event instanceof GuardsCheckEnd && event.shouldActivate) {
          const targetUrl = event.urlAfterRedirects.split('?')[0].split('#')[0];
          if (!isMiniMenuHost() && !targetUrl.startsWith('/mini-menu/')) {
            this.pageTitle.setForUrl(targetUrl);
          }
        }
        if (event instanceof NavigationEnd) {
          const url = event.urlAfterRedirects.split('?')[0].split('#')[0];
          this.isTourPage = /^\/tour(?:\/|$)/.test(url);
          this.isMiniMenuPage = isMiniMenuHost() || url.startsWith('/mini-menu/');
          // Check for special pages (why-hungery and pricing)
          this.isSpecialPage = url === '/why-hungery' || url === '/pricing';
          this.updateLoginState();

          const defaultSlug = miniMenuDefaultSlug();
          if (isMiniMenuHost() && url === '/' && defaultSlug) {
            this.router.navigateByUrl(`/${defaultSlug}`, { replaceUrl: true });
          }
        }
      })
    );

    // Subscribe to auth state changes
    this.subscriptions.add(
      this.authService.authStateChanged$.subscribe((isLoggedIn: boolean) => {
        this.isLogin = isLoggedIn;
        if (isLoggedIn) {
          // Update username or other user data when logged in
          // You might want to get this from a user service or stored data
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            this.userName = user.name || '';
          }
        } else {
          this.userName = "";
        }
      })
    );

    // Initialize login state
    this.updateLoginState();
  }

  ngAfterViewInit() {
    if (!this.isMiniMenuPage) {
      this.armFooterReveal();
    }
  }

  // Reveal the footer once the main content region has grown to a real size and then
  // stopped changing for a moment (i.e. content + images have settled). A hard cap
  // guarantees the footer still appears if observation is unavailable or content is sparse.
  private armFooterReveal() {
    const minContentHeight = 320;
    const settleMs = 700;
    const capMs = 12000;

    const reveal = () => {
      if (this.footerReady) {
        return;
      }
      this.zone.run(() => { this.footerReady = true; });
      this.teardownFooterReveal();
    };

    this.footerCapTimer = setTimeout(reveal, capMs);

    const host = this.mainContentRef?.nativeElement;
    if (!host || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      const queueFooterRevealCheck = () => {
        if (this.contentSettleTimer) {
          clearTimeout(this.contentSettleTimer);
        }
        this.contentSettleTimer = setTimeout(() => {
          if (host.offsetHeight >= minContentHeight) {
            reveal();
          }
        }, settleMs);
      };
      this.contentObserver = new ResizeObserver(queueFooterRevealCheck);
      this.contentObserver.observe(host);
      queueFooterRevealCheck();
    });
  }

  private teardownFooterReveal() {
    this.contentObserver?.disconnect();
    this.contentObserver = undefined;
    if (this.contentSettleTimer) { clearTimeout(this.contentSettleTimer); this.contentSettleTimer = null; }
    if (this.footerCapTimer) { clearTimeout(this.footerCapTimer); this.footerCapTimer = null; }
  }

  private updateLoginState() {
    this.isLogin = this.authService.isLoggedIn();
  }

  ngOnDestroy() {
    this.teardownFooterReveal();
    this.subscriptions.unsubscribe();
  }
}
